import styles from './style.module.scss';
import DateText from 'components/date-text';
import { buildClassName } from 'utils/build-class-name';
import { listingPageModalIds } from 'constants/test-constants';
import ListingStatus from 'components/listing-status';
import { StandardStatus } from '@zoocasa/go-search';
import React, { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { getSoldPriceLabel, getStatusClass } from './utils';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import ContactForPrice from './ContactForPrice';

import type { PriceHistoryRowProps } from './types';
import { useIsomorphicLayoutEffect } from 'types/react';

//#region sub-components

function SoldDate({ isBlurred, soldDate } : { isBlurred: boolean; soldDate?: Date | null }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const template = t(LOCALE_COMMON_PATHS.PRICE_HISTORY_SOLD_DATE_TEMPLATE, { defaultValue: 'MMM D, YYYY' });
  return (
    <section className={styles.stat}>
      <span className={`${styles.value} ${styles.large} ${isBlurred && styles.blur}`}>
        { isBlurred ?
          t(LOCALE_COMMON_PATHS.DEFAULTS_DATE_PLACEHOLDER, { defaultValue: '00/00/0000' }) :
          soldDate ?
            <DateText date={soldDate} template={template}/> :
            t(LOCALE_COMMON_PATHS.PRICE_HISTORY_NO_DATA_PLACEHOLDER, { defaultValue: '-' })
        }
      </span>
      <span className={styles.label}>
        {t(LOCALE_COMMON_PATHS.PRICE_HISTORY_SOLD_DATE_LABEL, { defaultValue: 'End Date' })}
      </span>
    </section>
  );
}

function SoldPrice({ isBlurred, soldPrice } : { isBlurred: boolean; soldPrice?: string | null }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <section className={buildClassName(styles.stat, styles['center-grid-item'])}>
      <span className={`${styles.value} ${styles.large} ${isBlurred && styles.blur}`}>
        { isBlurred ?
          t(LOCALE_COMMON_PATHS.DEFAULTS_PRICE_PLACEHOLDER, { defaultValue: '$XXX,XXX' }) :
          soldPrice
        }
      </span>
      <span className={styles.label}>
        {t(LOCALE_COMMON_PATHS.PRICE_HISTORY_SOLD_PRICE_LABEL, { defaultValue: 'Sold Price' })}
      </span>
    </section>
  );
}

function Status({ isBlurred, status, isRental } : { isBlurred: boolean; status: StandardStatus; isRental: boolean }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <section className={buildClassName(styles.stat, styles['center-grid-item'])}>
      <span className={`${styles.value} ${isBlurred && styles.blur}`}>
        { isBlurred ?
          t(LOCALE_COMMON_PATHS.PRICE_HISTORY_STATUS_PLACEHOLDER, { defaultValue: 'Top Secret' }) :
          <ListingStatus status={status} isRental={isRental} className={styles['status-label']} />
        }
      </span>
    </section>
  );
}

function ListDate({ isBlurred, listDate } : { isBlurred: boolean; listDate: Date }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const template = t(LOCALE_COMMON_PATHS.PRICE_HISTORY_LIST_DATE_TEMPLATE, { defaultValue: 'MMM D, YYYY' });
  return (
    <section className={styles.stat}>
      <span className={`${styles.value} ${isBlurred && styles.blur}`}>
        { isBlurred ?
          t(LOCALE_COMMON_PATHS.DEFAULTS_DATE_PLACEHOLDER, { defaultValue: '00/00/0000' }) :
          listDate ?
            <DateText date={listDate} template={template} /> :
            t(LOCALE_COMMON_PATHS.PRICE_HISTORY_NO_DATA_PLACEHOLDER, { defaultValue: '-' })
        }
      </span>
      <span className={styles.label}>
        {t(LOCALE_COMMON_PATHS.PRICE_HISTORY_LIST_DATE_LABEL, { defaultValue: 'List Date' })}
      </span>
    </section>
  );
}

function ListPrice({ isBlurred, listPrice } : { isBlurred: boolean; listPrice?: number | null }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const testId = listingPageModalIds.priceHistoryRowPrice;
  const { formatCurrency } = useI18nFormatting();
  return (
    <section className={buildClassName(styles.stat, styles['center-grid-item'])}>
      <span className={`${styles.value} ${isBlurred && styles.blur}`} data-testid={testId}>
        { isBlurred ?
          t(LOCALE_COMMON_PATHS.DEFAULTS_PRICE_PLACEHOLDER, { defaultValue: '$XXX,XXX' }) :
          listPrice !== null ? formatCurrency(listPrice) : t(LOCALE_COMMON_PATHS.PRICE_HISTORY_NO_DATA_PLACEHOLDER, { defaultValue: '-' })
        }
      </span>
      <span className={styles.label}>
        {t(LOCALE_COMMON_PATHS.PRICE_HISTORY_LIST_PRICE_LABEL, { defaultValue: 'List Price' })}
      </span>
    </section>
  );
}

function ViewButton({ isCurrentListing, onViewListingClick } : { isCurrentListing: boolean; onViewListingClick: () => void }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <section className={buildClassName(styles.stat, styles['center-grid-item'])}>
      { !isCurrentListing &&
        <button onClick={onViewListingClick}>
          {t(LOCALE_COMMON_PATHS.PRICE_HISTORY_VIEW_BUTTON_LABEL, { defaultValue: 'View' })}
        </button>
      }
    </section>
  );
}

//#endregion


export default function PriceHistoryRowMobile(props: PriceHistoryRowProps) {
  const { listing, isBlurred = false, isCurrentListing = false, testId, onViewListingClick, onContactForPriceClick } = props;
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatCurrency } = useI18nFormatting();
  const soldDate = listing.soldAt || listing.expiredAt;
  const soldPrice = getSoldPriceLabel(t, isCurrentListing, listing.isRental, listing.isAvailable, listing.soldPrice, formatCurrency);
  // We don't want to show the contact for price button when the price is hidden for the user or we have a valid price.
  const showContactForPrice = listing.price === 0 && !isBlurred;

  const onViewListingClickRef = React.useRef(onViewListingClick);

  useIsomorphicLayoutEffect(() => {
    onViewListingClickRef.current = onViewListingClick;
  });

  const handleViewListingClick = useCallback(() => {
    onViewListingClickRef?.current?.(listing.addressPath, listing.id);
  }, [listing.addressPath, listing.id]);

  return (
    <div className={`${styles.row} ${isCurrentListing && styles.active}`} data-testid={`${testId}-mobile`}>
      <span className={buildClassName(styles.node, getStatusClass(listing.standardStatus))}/>
      <div className={styles['mobile-row']}>
        <SoldDate isBlurred={isBlurred} soldDate={soldDate} />
        <SoldPrice isBlurred={isBlurred} soldPrice={soldPrice} />
        <Status isBlurred={isBlurred} status={listing.standardStatus} isRental={listing.isRental} />
        <ListDate isBlurred={isBlurred} listDate={listing.addedAt} />
        { showContactForPrice ?
          <ContactForPrice onClick={onContactForPriceClick} /> :
          <ListPrice isBlurred={isBlurred} listPrice={listing.price} />
        }
        <ViewButton
          isCurrentListing={isCurrentListing}
          onViewListingClick={handleViewListingClick}
        />
      </div>
    </div>
  );
}